import { FlowScreen } from "../../../models/AtticusScreenFlow";
import { OUTCOMES } from "../../../constants";
import { User } from "../../../models/User";
import { Estate } from "../../../models/Estate";
import { useAuthContext } from "../../../AuthContextWrapper";
import { AnimateYourPlanScreen } from "./AnimateYourPlanScreen";
import { RoleScreen, Outcomes as RoleOutcomes } from "./RoleScreen";
import {
  PassedYetScreen,
  Outcomes as PassedYetOutcomes,
} from "./PassedYetScreen";
import { DeceasedNameScreen } from "./DeceasedNameScreen";
import {
  RelationshipScreen,
  Outcomes as RelationshipOutcomes,
} from "./RelationshipScreen";
import {
  AdvisoryPositionScreen,
  Outcomes as AdvisoryPositionOutcomes,
} from "./AdvisoryPositionScreen";
import { SorryScreen } from "./SorryScreen";
import { DODScreen } from "./DODScreen";
import { WillScreen } from "./WillScreen";
import { LetterScreen, Outcomes as LettersOutcomes } from "./LettersScreen";
import {
  PrimaryHomeAssetScreen,
  Outcomes as AddressOutcomes,
} from "../shared/PrimaryHomeAssetScreen";
import { DoneSoFarScreen } from "./DoneSoFarScreen";
import { EstateAssetsScreen } from "./EstateAssetsScreen";
import { EstateTeamScreen } from "./EstateTeamScreen";
import { SimpleProbateScreen } from "./SimpleProbateScreen";
import { TurnAwayScreen } from "./TurnAwayScreen";
import { WelcomeScreen, Outcomes as WelcomeOutcomes } from "./WelcomeScreen";
import { HelpWithScreen } from "./HelpWithScreen";
import {
  PropertyOwnershipScreen,
  Outcomes as PropertyOwnershipOutcomes,
} from "../shared/PropertyOwnershipScreen";
import { PropertyValueScreen } from "../shared/PropertyValueScreen";
import {
  VeteranScreen,
  Outcomes as VeteranScreenOutcomes,
} from "./VeteranScreen";
import { VetAppreciationScreen } from "./VetAppreciationScreen";
import { AssetContextWrapper } from "../../../AssetContextWrapper";

export type FlowData = {
  user?: User;
  estate?: Estate;
};

export const loadData = (): FlowData => {
  // get the estate from MySQL
  const authContext = useAuthContext()

  return {
    // get the user from appContext - assumes the user is logged in
    user: authContext.user,
    estate: undefined,
  };
};

export enum Sections {
  AboutDeceased = "About the deceased",
  AboutEstate = "About the estate",
  YourPlan = "Start your plan",
}
export enum Screens {
  Welcome = "Welcome",
  HelpWith = "HelpWith",
  // ProbateType = "ProbateType",
  PassedYet = "PassedYet",
  Role = "Role",
  AdvisoryPosition = "AdvisoryPosition",
  TurnAway = "TurnAway",
  Relationship = "Relationship",
  DeceasedName = "DeceasedName",
  NonDeceasedName = "NonDeceasedName",
  NonDeceasedRelationship = "NonDeceasedRelationship",
  DeceasedVeteranStatus = "DeceasedVeteranStatus",
  DeceasedVeteranAppreciation = "DeceasedVeteranAppreciation",
  NonDeceasedVeteranStatus = "NonDeceasedVeteranStatus",
  NonDeceasedVeteranAppreciation = "NonDeceasedVeteranAppreciation",
  DeceasedAddress = "DeceasedAddress",
  NonDeceasedAddress = "NonDeceasedAddress",
  PropertyOwnership = "PropertyOwnership",
  PropertyValue = "PropertyValue",
  Will = "Will",
  Letters = "Letters",
  SimpleProbate = "SimpleProbate",
  Sorry = "Sorry",
  DateOfDeath = "DateOfDeath",
  DoneSoFar = "DoneSoFar",
  TheTeam = "TheTeam",
  EstateAssets = "EstateAssets",
  AnimatePlan = "AnimatePlan",
  YourPlan = "YourPlan",
}
export const screenFlow: FlowScreen[] = [
  {
    name: Screens.Welcome,
    screenComponent: <WelcomeScreen />,
    pathSuffix: "welcome",
    section: Sections.AboutDeceased,
    next: [
      { outcome: WelcomeOutcomes.getStarted, screen: Screens.PassedYet },
      { outcome: WelcomeOutcomes.alreadyStarted, screen: Screens.HelpWith },
    ],
    first: true,
  },
  {
    name: Screens.HelpWith,
    screenComponent: <HelpWithScreen />,
    pathSuffix: "need-help-with",
    section: Sections.AboutDeceased,
    next: [{ outcome: OUTCOMES.complete, screen: Screens.DeceasedName }],
  },
  // {
  //   name: Screens.ProbateType,
  //   screenComponent: <ProbateIntroScreen />,
  //   pathSuffix: "probate-type",
  //   section: Sections.AboutDeceased,
  //   next: [{ outcome: OUTCOMES.complete, screen: Screens.PassedYet }],
  // },
  {
    name: Screens.PassedYet,
    screenComponent: <PassedYetScreen />,
    pathSuffix: "passed-yet",
    section: Sections.AboutDeceased,
    next: [
      { outcome: PassedYetOutcomes.yes, screen: Screens.DeceasedName },
      { outcome: PassedYetOutcomes.no, screen: Screens.NonDeceasedName },
    ],
  },
  {
    name: Screens.DeceasedName,
    screenComponent: <DeceasedNameScreen />,
    pathSuffix: "deceased-name",
    section: Sections.AboutDeceased,
    next: [{ outcome: OUTCOMES.complete, screen: Screens.Relationship }],
  },
  {
    name: Screens.Relationship,
    screenComponent: <RelationshipScreen />,
    pathSuffix: "relationship",
    section: Sections.AboutDeceased,
    next: [
      { outcome: RelationshipOutcomes.spouse, screen: Screens.Sorry },
      { outcome: RelationshipOutcomes.child, screen: Screens.Sorry },
      { outcome: RelationshipOutcomes.parent, screen: Screens.Sorry },
      { outcome: RelationshipOutcomes.sibling, screen: Screens.Sorry },
      { outcome: RelationshipOutcomes.other, screen: Screens.Sorry },
      { outcome: RelationshipOutcomes.notRelated, screen: Screens.DateOfDeath },
      {
        outcome: RelationshipOutcomes.somethingElse,
        screen: Screens.DateOfDeath,
      },
    ],
  },
  {
    name: Screens.Sorry,
    screenComponent: <SorryScreen />,
    pathSuffix: "sorry",
    section: Sections.AboutDeceased,
    next: [{ outcome: OUTCOMES.complete, screen: Screens.DateOfDeath }],
  },
  {
    name: Screens.DateOfDeath,
    section: Sections.AboutDeceased,
    pathSuffix: "date-of-death",
    screenComponent: <DODScreen />,
    next: [
      { outcome: OUTCOMES.complete, screen: Screens.DeceasedVeteranStatus },
    ],
  },
  {
    name: Screens.NonDeceasedName,
    screenComponent: (
      <DeceasedNameScreen title="Who is the person who is going to pass?" />
    ),
    pathSuffix: "estate-name",
    section: Sections.AboutEstate,
    next: [
      { outcome: OUTCOMES.complete, screen: Screens.NonDeceasedRelationship },
    ],
  },
  {
    name: Screens.NonDeceasedRelationship,
    screenComponent: <RelationshipScreen />,
    pathSuffix: "estate-relationship",
    section: Sections.AboutEstate,
    next: [
      {
        outcome: RelationshipOutcomes.spouse,
        screen: Screens.NonDeceasedVeteranStatus,
      },
      {
        outcome: RelationshipOutcomes.child,
        screen: Screens.NonDeceasedVeteranStatus,
      },
      {
        outcome: RelationshipOutcomes.parent,
        screen: Screens.NonDeceasedVeteranStatus,
      },
      {
        outcome: RelationshipOutcomes.sibling,
        screen: Screens.NonDeceasedVeteranStatus,
      },
      {
        outcome: RelationshipOutcomes.other,
        screen: Screens.NonDeceasedVeteranStatus,
      },
      {
        outcome: RelationshipOutcomes.notRelated,
        screen: Screens.NonDeceasedVeteranStatus,
      },
      {
        outcome: RelationshipOutcomes.somethingElse,
        screen: Screens.NonDeceasedVeteranStatus,
      },
    ],
  },
  {
    name: Screens.DeceasedVeteranStatus,
    screenComponent: <VeteranScreen />,
    section: Sections.AboutDeceased,
    pathSuffix: "veteran-status",
    next: [
      {
        outcome: VeteranScreenOutcomes.yes,
        screen: Screens.DeceasedVeteranAppreciation,
      },
      { outcome: VeteranScreenOutcomes.no, screen: Screens.DeceasedAddress },
      {
        outcome: VeteranScreenOutcomes.notSure,
        screen: Screens.DeceasedAddress,
      },
    ],
  },
  {
    name: Screens.DeceasedVeteranAppreciation,
    screenComponent: <VetAppreciationScreen />,
    section: Sections.AboutDeceased,
    pathSuffix: "veteran-appreciation",
    next: [{ outcome: OUTCOMES.complete, screen: Screens.DeceasedAddress }],
  },
  {
    name: Screens.NonDeceasedVeteranStatus,
    screenComponent: <VeteranScreen notDeceased />,
    section: Sections.AboutEstate,
    pathSuffix: "estate-veteran-status",
    next: [
      {
        outcome: VeteranScreenOutcomes.yes,
        screen: Screens.NonDeceasedVeteranAppreciation,
      },
      { outcome: VeteranScreenOutcomes.no, screen: Screens.NonDeceasedAddress },
      {
        outcome: VeteranScreenOutcomes.notSure,
        screen: Screens.NonDeceasedAddress,
      },
    ],
  },
  {
    name: Screens.NonDeceasedVeteranAppreciation,
    screenComponent: <VetAppreciationScreen />,
    section: Sections.AboutEstate,
    pathSuffix: "estate-veteran-appreciation",
    next: [{ outcome: OUTCOMES.complete, screen: Screens.NonDeceasedAddress }],
  },
  {
    name: Screens.NonDeceasedAddress,
    section: Sections.AboutEstate,
    pathSuffix: "estate-address",
    screenComponent: (
      <AssetContextWrapper>
        <PrimaryHomeAssetScreen />
      </AssetContextWrapper>
    ),
    // TODO: confirm we want to shorten on-boarding for forms app customers
    // NOTE: if customer origin is FormsApp skip the rest of On-boarding after this
    next: [
      { outcome: AddressOutcomes.address, screen: Screens.PropertyOwnership },
      { outcome: AddressOutcomes.noAddress, screen: Screens.Will },
      { outcome: AddressOutcomes.skipToEnd, screen: Screens.AnimatePlan },
    ],
  },
  {
    name: Screens.DeceasedAddress,
    section: Sections.AboutDeceased,
    pathSuffix: "address",
    screenComponent: (
      <AssetContextWrapper>
        <PrimaryHomeAssetScreen
          title="Where did #deceasedFirstName# live (for tax and legal purposes)?"
          subTitle="This is typically their primary residence, the last known address, or what’s on their driver’s license."
        />
      </AssetContextWrapper>
    ),
    // TODO: confirm we want to shorten on-boarding for forms app customers
    // NOTE: if customer origin is FormsApp skip the rest of On-boarding after this
    next: [
      { outcome: AddressOutcomes.address, screen: Screens.PropertyOwnership },
      { outcome: AddressOutcomes.noAddress, screen: Screens.Will },
      { outcome: AddressOutcomes.skipToEnd, screen: Screens.AnimatePlan },
    ],
  },
  {
    name: Screens.PropertyOwnership,
    section: Sections.AboutEstate,
    pathSuffix: "ownership",
    screenComponent: (
      <AssetContextWrapper>
        <PropertyOwnershipScreen />
      </AssetContextWrapper>
    ),
    next: [
      {
        outcome: PropertyOwnershipOutcomes.yesIndividually,
        screen: Screens.PropertyValue,
      },
      {
        outcome: PropertyOwnershipOutcomes.yesJointly,
        screen: Screens.PropertyValue,
      },
      { outcome: PropertyOwnershipOutcomes.noRented, screen: Screens.Will },
      { outcome: PropertyOwnershipOutcomes.notSure, screen: Screens.Will },
      { outcome: PropertyOwnershipOutcomes.skip, screen: Screens.Will },
    ],
  },
  {
    name: Screens.PropertyValue,
    section: Sections.AboutEstate,
    pathSuffix: "property-value",
    screenComponent: (
      <AssetContextWrapper>
        <PropertyValueScreen />
      </AssetContextWrapper>
    ),
    next: [{ outcome: OUTCOMES.complete, screen: Screens.Will }],
  },
  {
    name: Screens.Will,
    section: Sections.AboutEstate,
    pathSuffix: "will",
    screenComponent: <WillScreen />,
    next: [{ outcome: OUTCOMES.complete, screen: Screens.Role }],
  },
  {
    // NOTE: will be skipped if Relationship is attorney, CPA, RE agent, others
    name: Screens.Role,
    section: Sections.AboutEstate,
    pathSuffix: "role",
    screenComponent: <RoleScreen />,
    next: [
      { outcome: RoleOutcomes.executor, screen: Screens.Letters },
      { outcome: RoleOutcomes.coExecutor, screen: Screens.Letters },
      { outcome: RoleOutcomes.personalRep, screen: Screens.Letters },
      { outcome: RoleOutcomes.corporateExecutor, screen: Screens.Letters },
      { outcome: RoleOutcomes.beneOrHeir, screen: Screens.TurnAway },
      { outcome: RoleOutcomes.skip, screen: Screens.Letters },
      { outcome: RoleOutcomes.somethingElse, screen: Screens.AdvisoryPosition },
    ],
  },
  {
    // NOTE: only show if role is Something Else
    name: Screens.AdvisoryPosition,
    section: Sections.AboutEstate,
    pathSuffix: "advisory-position",
    screenComponent: <AdvisoryPositionScreen />,
    next: [
      { outcome: AdvisoryPositionOutcomes.accountant, screen: Screens.Letters },
      { outcome: AdvisoryPositionOutcomes.attorney, screen: Screens.Letters },
      {
        outcome: AdvisoryPositionOutcomes.financialAdvisor,
        screen: Screens.Letters,
      },
      {
        outcome: AdvisoryPositionOutcomes.insuranceAgent,
        screen: Screens.Letters,
      },
      {
        outcome: AdvisoryPositionOutcomes.probateClerk,
        screen: Screens.Letters,
      },
      {
        outcome: AdvisoryPositionOutcomes.realEstateAgent,
        screen: Screens.Letters,
      },
      {
        outcome: AdvisoryPositionOutcomes.somethingElse,
        screen: Screens.Letters,
      },
    ],
  },
  {
    // NOTE: only show if role is bene or heir
    name: Screens.TurnAway,
    section: Sections.AboutEstate,
    pathSuffix: "non-exec",
    screenComponent: <TurnAwayScreen />,
    next: null,
  },
  {
    name: Screens.Letters,
    section: Sections.AboutEstate,
    pathSuffix: "letters",
    screenComponent: <LetterScreen />,
    next: [
      { outcome: LettersOutcomes.testamentary, screen: Screens.DoneSoFar },
      { outcome: LettersOutcomes.administration, screen: Screens.DoneSoFar },
      { outcome: LettersOutcomes.neither, screen: Screens.SimpleProbate },
    ],
  },
  {
    // NOTE: only show if letters is not testamentary or administration
    name: Screens.SimpleProbate,
    section: Sections.AboutEstate,
    pathSuffix: "simple-probate",
    screenComponent: <SimpleProbateScreen />,
    next: [{ outcome: OUTCOMES.complete, screen: Screens.DoneSoFar }],
  },
  {
    name: Screens.DoneSoFar,
    screenComponent: <DoneSoFarScreen />,
    section: Sections.AboutEstate,
    pathSuffix: "done-so-far",
    next: [{ outcome: OUTCOMES.complete, screen: Screens.EstateAssets }],
  },
  {
    name: Screens.EstateAssets,
    screenComponent: <EstateAssetsScreen />,
    pathSuffix: "estate-assets",
    section: Sections.AboutEstate,
    next: [{ outcome: OUTCOMES.complete, screen: Screens.TheTeam }],
  },
  {
    name: Screens.TheTeam,
    screenComponent: <EstateTeamScreen />,
    pathSuffix: "team",
    section: Sections.AboutEstate,
    next: [{ outcome: OUTCOMES.complete, screen: Screens.AnimatePlan }],
  },
  {
    name: Screens.AnimatePlan,
    screenComponent: <AnimateYourPlanScreen />,
    pathSuffix: "generating-your-plan",
    section: Sections.AboutEstate,
    // first: true, // make this the first screen to make debugging the animation screen easier
    next: null, // will record on-boarding complete and redirect to the Vue app when complete
  },
  // {
  //   name: Screens.YourPlan,
  //   screenComponent: <YourPlanScreen />,
  //   pathSuffix: "your-plan",
  //   section: Sections.YourPlan,
  //   next: null, //[{ outcome: YourPlanOutcomes.diy, screen: Screens.YourPlan }],
  // },
];
